import { apm } from '@elastic/apm-rum';
import * as Sentry from '@sentry/browser';
import { Amplify } from 'aws-amplify';
import { updatePassword as cognitoUpdatePassword, confirmResetPassword, confirmSignIn, fetchAuthSession, getCurrentUser, resetPassword, signIn, signOut, } from 'aws-amplify/auth';
import axios, { isAxiosError } from 'axios';
import { BehaviorSubject } from 'rxjs';
// 1 hour
const TOKEN_REFRESH_INTERVAL = 1000 * 60 * 60;
export class AuthService {
    static currentAuthoritySubject = new BehaviorSubject(null);
    static appAdminSubject = new BehaviorSubject(false);
    static appAdmin = false;
    static betaTesterSubject = new BehaviorSubject(false);
    static betaTester = false;
    static currentToken = '';
    static authorities = [];
    static authLoadingSubject = new BehaviorSubject(false);
    static config;
    static currentCognitoToken = null;
    static tokenRefreshTimer = null;
    constructor(axiosInstances, options) {
        if (options && options.cognito) {
            AuthService.config = options;
        }
        else {
            throw new Error('Missing Cognito ENVs');
        }
        if (options.settings.defaultApp === 'wayste-dashboard') {
            console.warn('No Authentication for wayste-dashboard');
            return;
        }
        AuthService.currentAuthoritySubject.subscribe((authority) => {
            if (authority) {
                AuthService.currentToken = authority.token;
                axiosInstances.mainInstance.defaults.headers.common['Authorization'] = `Bearer ${authority.token}`;
                axiosInstances.socket.setToken(`Bearer ${authority.token}`);
                if (apm.isActive()) {
                    apm.setUserContext({
                        id: authority.profileID,
                    });
                }
            }
            else {
                delete axiosInstances.mainInstance.defaults.headers.common['Authorization'];
                axiosInstances.socket.setToken(null);
                if (apm.isActive()) {
                    apm.setUserContext({});
                }
            }
        });
        AuthService.appAdminSubject.subscribe((authority) => {
            AuthService.appAdmin = authority;
            if (authority) {
                axiosInstances.appAdminInstance.defaults.headers.common['Authorization'] = `Bearer ${authority.token}`;
            }
            else {
                delete axiosInstances.appAdminInstance.defaults.headers.common['Authorization'];
            }
        });
        AuthService.betaTesterSubject.subscribe((betaTester) => {
            AuthService.betaTester = betaTester;
        });
        this.authSetup();
    }
    // auth flow
    // (1) check local cache for cognito token if it exists --> got to (3)
    // (2) user signs in with credentials --> send to cognito --> get cognito token back
    // (3) make request to profile service to getAvailableAuthorityTokens service which has internal auth token
    async authSetup() {
        AuthService.authLoadingSubject.next(true);
        // configure amplify
        Amplify.configure({
            Auth: {
                Cognito: {
                    userPoolEndpoint: AuthService.config.cognito.endpoint,
                    userPoolId: AuthService.config.cognito.UserPoolId,
                    userPoolClientId: AuthService.config.cognito.ClientId,
                },
            },
        });
        // (1) check local cache for cognito token if it exists
        try {
            const token = (await fetchAuthSession()).tokens?.idToken?.toString();
            if (!token) {
                throw new Error('No cognito token found');
            }
            AuthService.currentCognitoToken = token;
            const authorities = await this.fetchAvailableAuthorityTokens(token);
            AuthService.authorities = authorities;
            const filteredAuthorities = this.filterTokensByAppAndRole(authorities);
            if (filteredAuthorities.length > 0) {
                AuthService.currentAuthoritySubject.next(filteredAuthorities[0]);
                AuthService.tokenRefreshTimer = window.setTimeout(() => {
                    this.refreshAuth();
                }, TOKEN_REFRESH_INTERVAL);
                AuthService.authLoadingSubject.next(false);
                return;
            }
            else {
                throw new Error('No authorities found, you are not authorized to use this app. If you believe this is an error, please contact support.');
            }
        }
        catch (error) {
            this.signOut();
            // AuthService.currentAuthoritySubject.next(null); removed because emitting null twice causes issues
            AuthService.authLoadingSubject.next(false);
            console.warn('Token is not valid or does not exist, back to login', error);
        }
        AuthService.authLoadingSubject.next(false);
    }
    async refreshAuth() {
        if (AuthService.tokenRefreshTimer) {
            clearTimeout(AuthService.tokenRefreshTimer);
        }
        // (1) check local cache for cognito token if it exists
        try {
            const token = await this.getCurrentToken();
            AuthService.currentCognitoToken = token;
            const authorities = await this.fetchAvailableAuthorityTokens(token);
            AuthService.authorities = authorities;
            const filteredAuthorities = this.filterTokensByAppAndRole(authorities);
            if (filteredAuthorities.length > 0) {
                AuthService.currentAuthoritySubject.next(filteredAuthorities[0]);
                AuthService.tokenRefreshTimer = window.setTimeout(() => {
                    this.refreshAuth();
                }, TOKEN_REFRESH_INTERVAL);
            }
            else {
                throw new Error('No authorities found, you are not authorized to use this app. If you believe this is an error, please contact support.');
            }
        }
        catch (error) {
            this.signOut();
            console.warn('Token is not valid or does not exist, back to login', error);
        }
    }
    get currentAuthority() {
        return AuthService.currentAuthoritySubject.asObservable();
    }
    get currentToken() {
        return AuthService.currentToken;
    }
    get getCurrentCognitoToken() {
        return AuthService.currentCognitoToken;
    }
    get loading() {
        return AuthService.authLoadingSubject.asObservable();
    }
    get appAdmin() {
        return AuthService.appAdminSubject.asObservable();
    }
    get betaTester() {
        return AuthService.betaTesterSubject.asObservable();
    }
    get isBetaTester() {
        return AuthService.betaTester;
    }
    get currentAppAdmin() {
        return AuthService.appAdmin;
    }
    async login({ email, password }) {
        // (2) user signs in with credentials --> send to cognito --> get cognito token back
        AuthService.authLoadingSubject.next(true);
        try {
            const loginResult = await this.authenticateUser(email, password);
            // const res = await signIn({ username: email, password: password, options: { authFlowType: 'USER_PASSWORD_AUTH' } });
            if (loginResult.isSignedIn) {
                const cognitoToken = (await fetchAuthSession()).tokens?.idToken?.toString();
                if (!cognitoToken) {
                    throw new Error('User Signed in but does not have a cognito token');
                }
                const authorities = await this.fetchAvailableAuthorityTokens(cognitoToken);
                AuthService.currentCognitoToken = cognitoToken;
                const filteredAuthorities = this.filterTokensByAppAndRole(authorities);
                if (filteredAuthorities.length > 0) {
                    AuthService.currentAuthoritySubject.next(filteredAuthorities[0]);
                    AuthService.authLoadingSubject.next(false);
                    return;
                }
                else {
                    throw new Error('Something likely when wrong when creating your user. Please contact support.');
                }
            }
            else if (loginResult.challengeName === 'NEW_PASSWORD_REQUIRED') {
                AuthService.authLoadingSubject.next(false);
                return {
                    // cognitoUser: await getCurrentUser(),
                    challengeName: 'NEW_PASSWORD_REQUIRED',
                    sessionUserAttributes: {},
                };
            }
        }
        catch (error) {
            console.error('waysteClient login err', error);
            AuthService.authLoadingSubject.next(false);
            throw error;
        }
    }
    async signUp({ email }) {
        if (AuthService.config.settings.defaultApp !== 'customer-portal') {
            throw Error('Not implemented');
        }
        try {
            const res = await axios.post(AuthService.config.API.baseApiUrl + '/profile/customer/signup', {
                email,
            });
            return res.data;
        }
        catch (error) {
            if (isAxiosError(error) && error.response?.status === 409) {
                // no point in logging this error to sentry
                throw error;
            }
            Sentry.setUser({
                email,
            });
            Sentry.captureException(error);
            Sentry.setUser(null);
            throw error;
        }
    }
    async newPasswordChallenge({ newPassword }) {
        AuthService.authLoadingSubject.next(true);
        await confirmSignIn({
            challengeResponse: newPassword,
        });
        const cognitoToken = (await fetchAuthSession()).tokens?.idToken?.toString();
        if (!cognitoToken) {
            throw new Error('User Signed in but does not have a cognito token');
        }
        const authorities = await this.fetchAvailableAuthorityTokens(cognitoToken);
        if (authorities.length > 0) {
            AuthService.currentAuthoritySubject.next(this.filterTokensByAppAndRole(authorities)[0]);
            AuthService.authLoadingSubject.next(false);
            return;
        }
        else {
            AuthService.authLoadingSubject.next(false);
            throw 'Something likely when wrong when creating your user. Please contact support.';
        }
    }
    async signOut() {
        try {
            await signOut();
            // do sign out stuff then
            AuthService.authorities = [];
            AuthService.currentAuthoritySubject.next(null);
            AuthService.appAdminSubject.next(false);
        }
        catch (error) {
            console.error('Error signing out', error);
        }
    }
    async updatePassword({ oldPassword, newPassword }) {
        await cognitoUpdatePassword({ oldPassword, newPassword });
    }
    async verifyForgotPasswordCode({ userName, code, password }) {
        confirmResetPassword({ username: userName, newPassword: password, confirmationCode: code });
    }
    async forgotPassword({ email }) {
        return await resetPassword({ username: email });
    }
    /**
     *
     * @deprecated
     * @param _email - email or username of the user you want to get a cognito user for
     * @returns CognitoUser
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    cognitoUser(_email) {
        throw new Error('Deprecated');
    }
    getAuthorities() {
        return AuthService.authorities;
    }
    getAvailableRoles() {
        return AuthService.authorities.map((authority) => {
            return authority.roles[0];
        });
    }
    filterTokensByAppAndRole(authorities) {
        // first filter by app
        const filteredAuthorities = authorities.filter((authority) => {
            return authority.application === AuthService.config.settings.defaultApp;
        });
        // then filter by role
        if (AuthService.config.settings.defaultRole) {
            const filteredByRole = filteredAuthorities.filter((authority) => {
                // we just checked this above, but typescript doesn't know that :D
                //eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                return authority.roles.includes(AuthService.config.settings.defaultRole);
            });
            if (filteredByRole.length > 0) {
                return filteredByRole;
            }
        }
        return filteredAuthorities;
    }
    async authenticateUser(username, password) {
        const res = await signIn({
            username,
            password,
            options: AuthService.config.cognito.endpoint ? { authFlowType: 'USER_PASSWORD_AUTH' } : undefined,
        });
        if (res.isSignedIn) {
            return { isSignedIn: true };
        }
        if (res.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            return { isSignedIn: false, challengeName: 'NEW_PASSWORD_REQUIRED' };
        }
        throw new Error('Something went wrong');
    }
    async fetchAvailableAuthorityTokens(cognitoToken) {
        try {
            const response = await axios.post(AuthService.config.API.baseApiUrl + '/profile/auth', {
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                    Expires: '0',
                },
                cognitoToken: cognitoToken,
            });
            const authorities = response.data;
            if (authorities) {
                AuthService.authorities = authorities;
                // check if any or the authorities are BETA_TESTER
                const betaTesterAuthorities = authorities.filter((authority) => {
                    return authority.roles.includes('BETA_TESTER');
                });
                if (betaTesterAuthorities.length > 0) {
                    AuthService.betaTesterSubject.next(true);
                }
                else {
                    AuthService.betaTesterSubject.next(false);
                }
                // check if any or the authorities are APP_ADMIN
                const appAdminAuthorities = authorities.filter((authority) => {
                    return authority.roles.includes('APP_ADMIN');
                });
                if (appAdminAuthorities.length > 0) {
                    AuthService.appAdminSubject.next(appAdminAuthorities[0]);
                }
            }
            return authorities;
        }
        catch (error) {
            console.warn(error);
            await signOut();
            throw new Error('No tokens found, something likely went wrong when creating your user. Please contact support.');
        }
    }
    async getCurrentToken() {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
            throw new Error('No User');
        }
        const session = await fetchAuthSession();
        if (!session) {
            throw new Error('No Session');
        }
        return session.tokens?.idToken?.toString() || '';
    }
}
//# sourceMappingURL=auth.service.js.map